import { useEffect, useState } from 'react';

const POLL_INTERVAL = 50;
const LOAD_TIMEOUT = 20000;

/**
 * Re-render once the selected map provider's browser library has loaded.
 *
 * The map scripts are included with the `async` attribute (see
 * util/includeScripts.js), so they have usually NOT executed yet during the
 * first render. Without this hook, the render-time `isMapsLibLoaded()` guards
 * would latch onto that first "not loaded" result and never recover, leaving
 * the map area blank for the rest of the page's life.
 *
 * @param {Function} isMapsLibLoaded provider-specific readiness predicate
 * @return {Boolean} whether the map library is available
 */
export const useMapsLibLoaded = isMapsLibLoaded => {
  const [isLoaded, setIsLoaded] = useState(() => !!isMapsLibLoaded());

  useEffect(() => {
    if (isLoaded) {
      return undefined;
    }

    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      if (isMapsLibLoaded()) {
        window.clearInterval(intervalId);
        setIsLoaded(true);
      } else if (Date.now() - startedAt > LOAD_TIMEOUT) {
        // Give up polling: the script is either blocked or failed to load, and
        // the guards will keep rendering their non-map fallback.
        window.clearInterval(intervalId);
      }
    }, POLL_INTERVAL);

    return () => window.clearInterval(intervalId);
  }, [isLoaded, isMapsLibLoaded]);

  return isLoaded;
};
